import { useTranslation } from "react-i18next";
import type { LeaveBalance } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Calendar, Clock, TrendingDown, AlertCircle } from "lucide-react";

interface LeaveBalanceCardProps {
  balance: LeaveBalance;
}

export function LeaveBalanceCard({ balance }: LeaveBalanceCardProps) {
  const { t } = useTranslation();
  const {
    leaveType,
    leaveTypeLabel,
    leaveTypeColor,
    totalDays,
    carryOverDays,
    usedDays,
    pendingDays,
    availableNow,
    carryOverExpiry,
    countsAgainstBalance,
  } = balance;

  const grandTotal = (totalDays ?? 0) + carryOverDays;
  const progressPct =
    grandTotal > 0 ? Math.min(100, (usedDays / grandTotal) * 100) : 0;
  const isLow = countsAgainstBalance && (availableNow ?? 0) <= 2;

  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      {/* Top accent strip */}
      <div className="h-1" style={{ backgroundColor: leaveTypeColor }} />

      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-foreground truncate">
            {t(`leave.types.${leaveType}`, { defaultValue: leaveTypeLabel })}
          </span>
          {isLow && (
            <AlertCircle className="h-4 w-4 text-orange-500 shrink-0" />
          )}
        </div>

        {/* Big number */}
        <div className="flex items-baseline gap-1.5">
          <span className="text-3xl font-bold text-foreground" style={{ color: leaveTypeColor }}>
            {countsAgainstBalance ? availableNow : usedDays}
          </span>
          <span className="text-xs text-muted-foreground">
            {countsAgainstBalance
              ? t("leave.daysLeft", { total: grandTotal })
              : t("leave.approvedDaysIncludingUpcoming")}
          </span>
        </div>

        {/* Progress bar */}
        {countsAgainstBalance && grandTotal > 0 && (
          <Progress
            value={progressPct}
            className="h-1.5"
            style={{ "--progress-fg": leaveTypeColor } as React.CSSProperties}
          />
        )}

        {/* Stats footer */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground pt-0.5">
          {countsAgainstBalance && (
            <>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {totalDays} {t("leave.total")}
              </span>
              <span className="flex items-center gap-1">
                <TrendingDown className="h-3 w-3" />
                {usedDays} {t("leave.approvedDaysIncludingUpcoming")}
              </span>
            </>
          )}
          {pendingDays > 0 && (
            <span className="flex items-center gap-1 text-amber-600 font-medium">
              <Clock className="h-3 w-3" />
              {pendingDays} {t("leave.pending")}
            </span>
          )}
          {carryOverDays > 0 && (
            <span className="flex items-center gap-1 font-medium" style={{ color: leaveTypeColor }}>
              +{carryOverDays} {t("leave.carryOver")}
              {carryOverExpiry && (
                <span className="text-muted-foreground font-normal">
                  ({t("leave.expires")}{" "}
                  {new Date(carryOverExpiry).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                  })}
                  )
                </span>
              )}
            </span>
          )}
        </div>

        {!countsAgainstBalance && (
          <p className="text-xs text-muted-foreground">{t("leave.unlimited", { defaultValue: "No annual limit" })}</p>
        )}
      </div>
    </div>
  );
}
